import { IApiClaimsHistory, IContractData } from '../interfaces/IJob';
import { IPlanStoryContext, IPlanStoryStat, MyPlanStoryScenario } from '../interfaces/IMyPlanStory';
import { CONTRACT_STATUS_EXPIRED } from '../components/pages/my-plan/my-plan.constants';
import { CAN } from './constants';

// A cancelled service request never delivered value, so it must not count as a
// claim for MyPlanStory purposes — a member whose only activity this year was
// cancelled jobs should get the same 0-claims narrative as one with none at all.
function filterOutCancelledJobs(jobsData: IApiClaimsHistory[]): IApiClaimsHistory[] {
  return jobsData.filter(job => job.serviceJobStatus !== CAN);
}

// jobsData can include jobs carried over from a prior contract period (e.g. a
// renewed contract's history includes last year's claims too) — those must
// never count toward "this year"'s stats. requestDateFormat and the base
// contract's effectiveDate/expirationDate are all "YYYYMMDD" strings, so a
// plain lexical comparison is a valid date comparison. A job missing its
// request date can't be placed in the current period, so it's excluded.
function filterJobsToCurrentContractPeriod(
  jobsData: IApiClaimsHistory[],
  effectiveDate: string,
  expirationDate: string
): IApiClaimsHistory[] {
  return jobsData.filter(job => {
    const requestDate = job.requestDateFormat;
    return !!requestDate && requestDate >= effectiveDate && requestDate <= expirationDate;
  });
}

/**
 * Single source of truth for which MyPlanStory scenario applies, given the
 * same eligibility signals the dashboard already computes for its standalone
 * alerts/toast (showComeBackAlert === dashboard's showToastForPlanStatus,
 * showPaymentMethodAlert === dashboard's showPaymentMethodAlert, showRenewalCta
 * === DataService.showRealEstateRenewalBanner()). Change precedence here only.
 *
 * renewal is checked before payment to mirror dashboard.component.ts's own
 * standalone-banner precedence (`showPaymentMethodAlert && !showRealEstateRenewalAlert`
 * — the renewal banner suppresses the payment banner there), so this card's CTA
 * never contradicts whichever standalone banner is actually visible on the page.
 */
export function resolveMyPlanStoryScenario(
  showComeBackAlert: boolean,
  showPaymentMethodAlert: boolean,
  showRenewalCta: boolean
): MyPlanStoryScenario {
  if (showComeBackAlert) {
    return 'comeback';
  }
  if (showRenewalCta) {
    return 'renewal';
  }
  if (showPaymentMethodAlert) {
    return 'payment';
  }
  return 'none';
}

/**
 * Reduces contract/claims data to the aggregate, non-PII subset needed to
 * generate the MyPlanStory narrative. Never forward the raw IContractData
 * object (names, addresses, contract numbers on their own are fine, but the
 * full object carries customer/property PII) to the AI backend.
 */
export function buildPlanStoryContext(
  contractData: IContractData,
  jobsData: IApiClaimsHistory[],
  scenario: MyPlanStoryScenario = 'none',
  existingAlertMessage: string | null = null
): IPlanStoryContext {
  const base = contractData.baseContract;
  const renewal = contractData.renewalContract;
  const parsedTenure = parseInt(base.contractYear, 10);
  const currentPeriodJobs = filterJobsToCurrentContractPeriod(jobsData, base.effectiveDate, base.expirationDate);
  const countedJobs = filterOutCancelledJobs(currentPeriodJobs);
  const hasCancelledClaimsThisYear = currentPeriodJobs.some(job => job.serviceJobStatus === CAN);
  // completionDate is only set once a job actually finishes — anything still
  // in flight (accepted, scheduled, diagnosed, etc.) hasn't delivered value
  // yet and must not be narrated as a claim the plan already resolved.
  const completedJobs = countedJobs.filter(job => !!job.completionDate);
  const inProgressJobs = countedJobs.filter(job => !job.completionDate);

  const completedClaimEntries = buildClaimEntries(completedJobs);
  const inProgressClaimEntries = buildClaimEntries(inProgressJobs);
  return {
    contractNumber: base.contractNumber,
    clientId: base.clientId,
    contractStatus: base.contractStatus,
    memberTenureYears: Number.isFinite(parsedTenure) && parsedTenure > 0 ? parsedTenure : 1,
    claimsFiledThisYear: countedJobs.length,
    claimsCompletedThisYear: completedJobs.length,
    claimsInProgressThisYear: inProgressJobs.length,
    hasCancelledClaimsThisYear,
    claimedItemDescriptions: completedClaimEntries.map(entry => entry.description),
    claimCategories: completedClaimEntries.map(entry => entry.category),
    claimedItemUnitCounts: completedClaimEntries.map(entry => entry.unitCount),
    completedClaimsSameCategory: jobsShareSingleCategory(completedJobs),
    inProgressItemDescriptions: inProgressClaimEntries.map(entry => entry.description),
    claimsInProgressCategories: inProgressClaimEntries.map(entry => entry.category),
    inProgressItemUnitCounts: inProgressClaimEntries.map(entry => entry.unitCount),
    inProgressClaimsSameCategory: jobsShareSingleCategory(inProgressJobs),
    state: contractData.mailingAddress?.state || null,
    estimatedValueCovered: 0, // enriched server-side from claims payout data
    nextRenewalDate: formatYyyymmddToUs(renewal?.expirationDate || base.expirationDate || null),
    renewalDueSoon: scenario === 'renewal',
    scenario,
    existingAlertMessage,
  };
}

// Contract dates come back as a raw "YYYYMMDD" string (e.g. "20250423").
// Reformat to US "MM/DD/YYYY" once here so every consumer — the stat display,
// the fallback narrative, and the model itself — sees a presentable date
// instead of the raw digit string. Mirrors UtilitiesService.formatStrDate();
// duplicated locally since this module has no DI to reuse that service.
function formatYyyymmddToUs(dateStr: string | null | undefined): string | null {
  if (!dateStr || dateStr.length !== 8) {
    return dateStr || null;
  }
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${month}/${day}/${year}`;
}

/**
 * Deterministically formats the display stats from plan-story context.
 * Computed in code — never handed to the model to compute or reformat —
 * so a hallucinated total can never reach the UI.
 */
export function buildPlanStoryStats(context: IPlanStoryContext): IPlanStoryStat[] {
  const stats: IPlanStoryStat[] = [
    { label: 'Claims filed', value: context.claimsFiledThisYear > 0 ? String(context.claimsFiledThisYear) : 'Not yet' },
  ];
  if (context.claimsInProgressThisYear > 0) {
    stats.push({ label: 'In progress', value: String(context.claimsInProgressThisYear) });
  }
  if (context.estimatedValueCovered) {
    stats.push({ label: 'Est. value covered', value: `$${context.estimatedValueCovered.toLocaleString()}` });
  }
  if (context.nextRenewalDate) {
    // For an expired plan this date is when coverage lapsed (a past date), not
    // an upcoming renewal window — label it accordingly so the stat doesn't
    // contradict a narrative that's telling the member to renew now.
    const label = context.contractStatus === CONTRACT_STATUS_EXPIRED ? 'Plan expired on' : 'Next renewal';
    stats.push({ label, value: context.nextRenewalDate });
  }
  return stats;
}

// A job's primary trade category — the first model's itemTrade. Used (instead
// of flattening every model across every job) so a job's description and
// category can be paired from the same job and stay index-aligned.
function getJobPrimaryCategory(job: IApiClaimsHistory): string | null {
  return job.models?.[0]?.itemTrade || null;
}

// Builds description+category pairs from the SAME job, so e.g.
// claimedItemDescriptions[i]/claimCategories[i] (built from this for completed
// jobs) or inProgressItemDescriptions[i]/claimsInProgressCategories[i] (built
// from this for in-progress jobs) actually describe the same claim, as
// MYPLANSTORY_RENEWAL_SYSTEM_PROMPT assumes. Two prior implementations deduped
// these arrays independently (one from job.serviceDescription, the other
// flattened across job.models), which could silently pair an item with a
// different claim's category. A job with no model/trade data still
// contributes its description, paired with an empty-string category, rather
// than being dropped — the prompt is told to treat an empty category as
// "unknown" and fall back to a generic phrase rather than invent one.
//
// unitCount is how many distinct physical units were serviced under each
// description (e.g. two water heaters vs. one water heater serviced twice), so
// the prompt can pluralize the item only when the member really has more than one.
function buildClaimEntries(
  jobsData: IApiClaimsHistory[],
  limit = 5
): { description: string; category: string; unitCount: number }[] {
  const entries: { description: string; category: string; unitKeys: Set<string> }[] = [];
  const entriesByDescription = new Map<string, { description: string; category: string; unitKeys: Set<string> }>();
  for (const job of jobsData) {
    const description = job.serviceDescription;
    if (!description) {
      continue;
    }
    let entry = entriesByDescription.get(description);
    if (!entry) {
      if (entries.length >= limit) {
        continue;
      }
      entry = { description, category: getJobPrimaryCategory(job) || '', unitKeys: new Set<string>() };
      entriesByDescription.set(description, entry);
      entries.push(entry);
    }
    const unitKey = getJobPrimaryUnitKey(job);
    if (unitKey) {
      entry.unitKeys.add(unitKey);
    }
  }
  // No unit identifiers at all → assume a single unit, so the narrative never
  // pluralizes an item the member may only own one of.
  return entries.map(({ description, category, unitKeys }) => ({
    description,
    category,
    unitCount: Math.max(unitKeys.size, 1),
  }));
}

// Identifies the physical unit a job serviced — the contract's covered-product
// row, falling back to the serial number. Null when neither is present.
function getJobPrimaryUnitKey(job: IApiClaimsHistory): string | null {
  const model = job.models?.[0];
  return model?.productRowNumber || model?.serialNumber || null;
}

// True when 2+ jobs in the given set all share exactly one trade category —
// signals a repeat need in one area (e.g. plumbing) rather than a single
// one-off fix, so the narrative can reference the pattern instead of naming
// just one item. False for a single job — "same category" only means
// something once there's more than one to compare.
function jobsShareSingleCategory(jobsData: IApiClaimsHistory[]): boolean {
  if (jobsData.length < 2) {
    return false;
  }
  const categories = jobsData.map(getJobPrimaryCategory).filter((trade): trade is string => !!trade);
  return categories.length === jobsData.length && new Set(categories).size === 1;
}
